import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('access_token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API calls
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  logout: () => api.post('/auth/logout'),
  getCurrentUser: () => api.get('/auth/me'),
  getCompanies:   ()           => api.get('/auth/companies'),
  switchCompany:  (company_id) => api.post('/auth/switch-company', { company_id }),
  changePassword: (passwordData) => api.post('/auth/change-password', passwordData),
};

// Attendance API calls
export const attendanceAPI = {
  clockIn: () => api.post('/attendance/clock-in'),
  clockOut: () => api.post('/attendance/clock-out'),
  getTodayStatus: () => api.get('/attendance/today-status'),
  getMyRecords: (params) => api.get('/attendance/my-records', { params }),
  getUserRecords: (userId, params) => api.get(`/attendance/records/${userId}`, { params }),
  getHrDailyRecords: (params) => api.get('/attendance/hr/daily-records', { params }),
  getRecordHistory:   (recordId)          => api.get(`/attendance/records/${recordId}/history`),
  getChangeHistory:   (params)            => api.get('/attendance/change-history', { params }),
  getRawTransactions: (userId, workday)   => api.get('/attendance/raw-transactions', { params: { user_id: userId, workday } }),
  getOTVerification: (params) => api.get('/attendance/hr/ot-verification', { params }),
  hrBatchUpdateRecords: (data) => api.post('/attendance/hr/batch-update', data),
  getManagerTeamRecords: (params) => api.get('/attendance/manager/team-records', { params }),
};

// Overtime API calls
export const overtimeAPI = {
  requestOvertime: (overtimeData) => api.post('/attendance/overtime/request', overtimeData),
  getMyOvertimeRequests: () => api.get('/attendance/overtime/my-requests'),
  getAllOvertimeRequests: (params) => api.get('/attendance/overtime/all-requests', { params }),
  cancelOvertimeRequest: (overtimeId) => api.post(`/attendance/overtime/cancel/${overtimeId}`),
  approveOvertime: (overtimeId, status, managerRemark) => api.post(`/attendance/overtime/approve/${overtimeId}`, { status, manager_remark: managerRemark }),
  bulkApproveOvertime: (overtimeIds, status, managerRemark) => api.post('/attendance/overtime/bulk-approve', { overtime_ids: overtimeIds, status, manager_remark: managerRemark }),
  requestOvertimeOnBehalf: (data) => api.post('/attendance/overtime/request-on-behalf', data),
  updateOvertime: (id, data) => api.put(`/attendance/overtime/${id}/edit`, data),
  getOvertimeHistory: (id) => api.get(`/attendance/overtime/${id}/history`),
};

// Users API calls (HR/Admin only)
export const usersAPI = {
  getUsers: (params) => api.get('/users/users', { params }),
  getUser: (userId) => api.get(`/users/users/${userId}`),
  createUser: (userData) => api.post('/users/users', userData),
  updateUser: (userId, userData) => api.put(`/users/users/${userId}`, userData),
  deleteUser: (userId) => api.delete(`/users/users/${userId}`),
  getShifts: () => api.get('/users/shifts'),
  batchUpdateUsers: (data) => api.post('/users/users/batch-update', data),
  // Org hierarchy (read-only for HR)
  getDepartments: () => api.get('/users/departments'),
  getDeptSubdepts: (deptId) => api.get(`/users/departments/${deptId}/subdepts`),
  getSubdeptSections: (subdeptId) => api.get(`/users/subdepts/${subdeptId}/sections`),
  getSectionSubsections: (sectionId) => api.get(`/users/sections/${sectionId}/subsections`),
};

// Analytics API calls (Manager / HR / Admin)
export const analyticsAPI = {
  getPunctuality:            (start, end)                    => api.get('/analytics/punctuality',              { params: { start, end } }),
  getPunctualityDetail:      (start, end, dept)              => api.get('/analytics/punctuality/detail',       { params: { start, end, dept } }),
  getAbsenteeism:            (start, end)                    => api.get('/analytics/absenteeism',              { params: { start, end } }),
  getAbsenteeismDetail:      (start, end, dept)              => api.get('/analytics/absenteeism/detail',       { params: { start, end, dept } }),
  getOvertime:               (start, end)                    => api.get('/analytics/overtime',                 { params: { start, end } }),
  getOvertimeDetail:         (start, end, dept, subdept, section) => api.get('/analytics/overtime/detail',    { params: { start, end, dept, subdept, section } }),
  getOvertimeDaily:          (start, end)                    => api.get('/analytics/overtime/daily',           { params: { start, end } }),
  getOvertimeSubdeptSummary: (start, end, dept)              => api.get('/analytics/overtime/subdept-summary', { params: { start, end, dept } }),
  getOvertimeSectionSummary: (start, end, dept, subdept)     => api.get('/analytics/overtime/section-summary', { params: { start, end, dept, subdept } }),
};

// Reporting API calls (HR/Admin only)
export const reportingAPI = {
  getAttendanceAllowance: (year, month) =>
    api.get('/reporting/attendance-allowance', { params: { year, month } }),
  getMealAllowance: (year, month) =>
    api.get('/reporting/meal-allowance', { params: { year, month } }),
  getShiftAllowance: (year, month) =>
    api.get('/reporting/shift-allowance', { params: { year, month } }),
  getLateness: (year, month) =>
    api.get('/reporting/lateness', { params: { year, month } }),
  getOvertimeReport: (year, month) =>
    api.get('/reporting/overtime', { params: { year, month } }),
  getOvertimeMealAllowance: (year, month) =>
    api.get('/reporting/overtime-meal-allowance', { params: { year, month } }),
};

// Shift History API calls (HR/Admin only)
export const shiftHistoryAPI = {
  getUsersWithHistory: () => api.get('/users/shift-history/users'),
  getUserHistory: (userId) => api.get(`/users/users/${userId}/shift-history`),
  addEntry: (userId, data) => api.post(`/users/users/${userId}/shift-history`, data),
  updateEntry: (historyId, data) => api.put(`/users/shift-history/${historyId}`, data),
  deleteEntry: (historyId) => api.delete(`/users/shift-history/${historyId}`),
};

// Processing API calls (Admin only)
export const processingAPI = {
  getCompanies: () => api.get('/processing/companies'),
  processAllPending: (data) => api.post('/processing/process-all-pending', data),
  getJobStatus: (jobId) => api.get(`/processing/job-status/${jobId}`),
  syncUsers: () => api.post('/processing/sync-users'),
  syncLeaves: () => api.post('/processing/sync-leaves'),
};

// Bulletin Board API calls
export const bulletinAPI = {
  getPosts:    (params)   => api.get('/bulletin/posts', { params }),
  createPost:  (data)     => api.post('/bulletin/posts', data),
  updatePost:  (id, data) => api.put(`/bulletin/posts/${id}`, data),
  deletePost:  (id)       => api.delete(`/bulletin/posts/${id}`),
  uploadImage: (formData) => api.post('/bulletin/uploads', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
};

// Admin API calls (Admin only)
export const adminAPI = {
  // Shifts
  getShifts: () => api.get('/admin/shifts'),
  createShift: (data) => api.post('/admin/shifts', data),
  updateShift: (shiftId, data) => api.put(`/admin/shifts/${shiftId}`, data),
  deleteShift: (shiftId) => api.delete(`/admin/shifts/${shiftId}`),
  // Departments
  getDepartments: () => api.get('/admin/departments'),
  createDepartment: (data) => api.post('/admin/departments', data),
  updateDepartment: (deptId, data) => api.put(`/admin/departments/${deptId}`, data),
  deleteDepartment: (deptId) => api.delete(`/admin/departments/${deptId}`),
  // Sub-departments
  getSubdepts: (deptId) => api.get(`/admin/departments/${deptId}/subdepts`),
  createSubdept: (deptId, data) => api.post(`/admin/departments/${deptId}/subdepts`, data),
  updateSubdept: (subdeptId, data) => api.put(`/admin/subdepts/${subdeptId}`, data),
  deleteSubdept: (subdeptId) => api.delete(`/admin/subdepts/${subdeptId}`),
  // Public Holidays
  getHolidays: (params) => api.get('/admin/holidays', { params }),
  createHoliday: (data) => api.post('/admin/holidays', data),
  updateHoliday: (holidayId, data) => api.put(`/admin/holidays/${holidayId}`, data),
  deleteHoliday: (holidayId) => api.delete(`/admin/holidays/${holidayId}`),
  // Sections (CRUD)
  getSections: (subdeptId) => api.get(`/admin/subdepts/${subdeptId}/sections`),
  createSection: (subdeptId, data) => api.post(`/admin/subdepts/${subdeptId}/sections`, data),
  updateSection: (sectionId, data) => api.put(`/admin/sections/${sectionId}`, data),
  deleteSection: (sectionId) => api.delete(`/admin/sections/${sectionId}`),
  // Subsections (CRUD)
  getSubsections: (sectionId) => api.get(`/admin/sections/${sectionId}/subsections`),
  createSubsection: (sectionId, data) => api.post(`/admin/sections/${sectionId}/subsections`, data),
  updateSubsection: (subsectionId, data) => api.put(`/admin/subsections/${subsectionId}`, data),
  deleteSubsection: (subsectionId) => api.delete(`/admin/subsections/${subsectionId}`),
};

export default api;
